import { Button, Column, Icon, Row, Text } from "@amzn/meridian";
import React, { useMemo, useEffect } from "react";
import QuestionResultGroup from "./QuestionResultGroup";
import ProgressBreadcrumb from "src/components/UI/ProgressBreadcrumb";
import { COLORS } from "src/constants/colors";
import { useAppDispatch, useAppSelector } from "src/store/store";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import { groupResultsForDisplay } from "./utils";
import { Result, Question } from "src/data/types";
import { getInspectionById, fetchPresignedUrlsForInspection } from "src/store/inspectionSlice";
import { setInspectionResults } from "src/store/inspectionResultsSlice";

type InspectionDetailsViewMode = "review" | "view-only" | "view-editable";

type InspectionDetailsViewProps = {
  mode: InspectionDetailsViewMode;
  templateName?: string;
  inspectionResults: Result[];
  questions?: Question[];
  inspectionId: string;
  onBack?: () => void;
  onSubmit?: () => void;
  onEdit?: () => void;
  showProgressBreadcrumb?: boolean;
  currentStep?: number;
};

const InspectionDetailsView: React.FC<InspectionDetailsViewProps> = ({
  mode,
  templateName,
  inspectionResults,
  questions,
  inspectionId,
  onBack,
  onSubmit,
  onEdit,
  showProgressBreadcrumb = true,
  currentStep = 1,
}) => {
  const dispatch = useAppDispatch();
  const [refreshKey, setRefreshKey] = React.useState(0);
  const selectedInspectionDetail = useAppSelector((state) => state.inspection.selectedInspectionDetail);
  const presignedUrlsFetched = useAppSelector((state) => state.inspection.presignedUrlsFetched);
  const fetchInitiatedRef = React.useRef<Set<string>>(new Set());

  // Check for pending results
  const pendingResults = useMemo(() => {
    return inspectionResults.filter((r) => r.aiResult === "PENDING");
  }, [inspectionResults]);

  const hasPending = pendingResults.length > 0;

  // Group results by question for display
  const groupedResults = useMemo(() => {
    if (!questions) return [];
    return groupResultsForDisplay(inspectionResults as any, questions as any);
  }, [inspectionResults, questions]);

  // Fetch presigned URLs when inspection loads (only once per inspection)
  useEffect(() => {
    if (
      inspectionId &&
      selectedInspectionDetail && 
      selectedInspectionDetail.inspectionId === inspectionId &&
      selectedInspectionDetail.results &&
      selectedInspectionDetail.results.length > 0 &&
      !presignedUrlsFetched.includes(inspectionId) &&
      !fetchInitiatedRef.current.has(inspectionId)
    ) {
      console.log('Fetching presigned URLs for inspection:', inspectionId);
      fetchInitiatedRef.current.add(inspectionId);
      dispatch(fetchPresignedUrlsForInspection(selectedInspectionDetail));
    }
  }, [inspectionId, selectedInspectionDetail, presignedUrlsFetched.length, dispatch]);

  // Callback to refresh inspection data
  const handleRefresh = React.useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Polling effect - refresh inspection when there are pending results
  useEffect(() => {
    if (hasPending && inspectionId) {
      const interval = setInterval(async () => {
        // Poll existing GET API to refresh results
        const result = await dispatch(getInspectionById(inspectionId)).unwrap();

        // Sync results to inspection slice
        dispatch(setInspectionResults(result.results));
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [hasPending, inspectionId, dispatch]);

  // Determine if submit button should be disabled
  const isSubmitDisabled = mode === "review" && hasPending;

  // Determine header text based on mode
  const headerText =
    mode === "review" ? "Review & Submit" : "Inspection Details";

  return (
    <Column
      className="questions-page"
      height="100vh"
      backgroundColor={COLORS.DARKER_WHITE}
    >
      <Column height="fill">
        <Column
          width="100%"
          backgroundColor={COLORS.WHITE}
          spacingInset="600 500 400"
        >
          <Row>
            {onBack && (
              <div
                style={{ cursor: "pointer", marginLeft: "50px" }}
                onClick={onBack}
              >
                <Icon tokens={chevronLeftLargeTokens} color="primary" />
              </div>
            )}
            <Text type="h200">{headerText}</Text>
          </Row>
          <Column>
            <Row>
              {templateName && <Text type="b500">{templateName}</Text>}
            </Row>
          </Column>
        </Column>

        {/* Progress Breadcrumb */}
        {showProgressBreadcrumb && (
          <Column spacingInset="400 500">
            <ProgressBreadcrumb
              steps={["Answer Questions", "Review Results", "Submit"]}
              currentStep={currentStep}
            />
          </Column>
        )}

        {/* Instructions Banner */}
        {mode === "review" && (
          <div
            style={{
              padding: "16px 20px",
              backgroundColor: "#F7F9FA",
              margin: "0 20px",
              borderRadius: "8px",
            }}
          >
            <Column spacing="100">
              <Text type="b300">Review your inspection results</Text>
              <Text type="b100" color="secondary">
                Check all answers below. Once confirmed, click Submit Inspection
                to finalize your report.
              </Text>
            </Column>
          </div>
        )}

        {/* Pending warning banner */}
        {hasPending && mode !== "view-editable" && (
          <Column
            spacingInset="300 500"
            backgroundColor="#FFF4E6"
            spacing="200"
          >
            <Row spacing="200" alignmentVertical="center">
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #FF9900",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <Text color="secondary">
                {pendingResults.length} photo
                {pendingResults.length > 1 ? "s" : ""} still analyzing...
              </Text>
            </Row>
          </Column>
        )}

        <Column spacingInset="none 500" spacing="300" backgroundColor={COLORS.DARKER_WHITE}>
          {groupedResults.map((group) => (
            <QuestionResultGroup
              key={`${group.questionId}-${refreshKey}`}
              questionText={group.questionText}
              questionOrder={group.questionOrder}
              inspectionId={inspectionId}
              instances={group.instances as any}
              onUpdate={handleRefresh}
            />
          ))}
        </Column>
      </Column>

      {/* Action Buttons */}
      <Column spacingInset="500" spacing="200" backgroundColor={COLORS.DARKER_WHITE}>
        {mode === "review" && onBack && onSubmit && (
          <>
            <Row widths={"fill"}>
              <Button type="tertiary" size="large" onClick={onBack}>
                Back
              </Button>
              <Button
                type="primary"
                disabled={isSubmitDisabled}
                size="large"
                onClick={onSubmit}
              >
                {hasPending ? "Analyzing..." : "Submit Inspection"}
              </Button>
            </Row>
            <Row alignmentHorizontal="center">
              <Text type="b100" color="secondary">
                Step 2 of 2 - Final Submission
              </Text>
            </Row>
          </>
        )}

        {mode === "view-editable" && onEdit && !hasPending && (
          <Button type="primary" size="large" onClick={onEdit}>
            Edit Inspection
          </Button>
        )}
      </Column>
    </Column>
  );
};

export default InspectionDetailsView;
